import { useEffect, useState } from 'react';
import type { SessionAnalyticsClient } from '@opengov/bnp-core';
import type { ShellBridgeLike } from '@opengov/analytics';
import { useFeatureFlagStore } from '@opengov/feature-flag/launchDarkly';
import { isPspMounted } from '../psp/isPspMounted';
import type {
  AnalyticsEntity,
  AnalyticsSession,
  StoriesAnalyticsClient
} from '../instrumentation/storiesAnalyticsRuntime';

type StoriesAnalyticsRuntime = typeof import('../instrumentation/storiesAnalyticsRuntime');
type LoadedAnalytics = { client: StoriesAnalyticsClient; runtime: StoriesAnalyticsRuntime };

type AnalyticsEnv = { ANALYTICS_KEY?: string; ANALYTICS_HOST?: string; ANALYTICS_ALLOWED_HOSTS?: string };

/** Injected into index.html by vite.config.ts — on integration builds only. */
const analyticsEnv = (): AnalyticsEnv =>
  (window as unknown as { process?: { env?: AnalyticsEnv } }).process?.env ?? {};

// One client per page: two would resolve two identities for one person.
let loaded: Promise<LoadedAnalytics> | null = null;

/** The shell bridge pspEntry.mount() stashed; undefined when standalone. */
const pspShellBridge = (): ShellBridgeLike | undefined =>
  (window as unknown as { __OG_STORIES_PSP_OPTIONS__?: { shell?: ShellBridgeLike } }).__OG_STORIES_PSP_OPTIONS__?.shell;

/** Standalone delivers with its own key; in PSP the shell delivers over the bridge. */
const loadAnalytics = (): Promise<LoadedAnalytics> => {
  if (!loaded) {
    const { ANALYTICS_KEY, ANALYTICS_HOST, ANALYTICS_ALLOWED_HOSTS } = analyticsEnv();
    loaded = import('../instrumentation/storiesAnalyticsRuntime').then((runtime) => ({
      runtime,
      client: isPspMounted()
        ? runtime.createStoriesShellAnalytics(pspShellBridge() ?? {})
        : runtime.createStoriesAnalytics({
            analyticsKey: ANALYTICS_KEY,
            analyticsHost: ANALYTICS_HOST,
            allowedHosts: ANALYTICS_ALLOWED_HOSTS
          })
    }));
  }
  return loaded;
};

// Resolved with the client once it is loaded — by StoriesNavBar standalone, by
// useTrackStoriesView in PSP. Never resolves when analytics is off, so
// trackStoriesEvent is then a no-op.
let resolveReady: (client: StoriesAnalyticsClient) => void = () => undefined;
const ready = new Promise<StoriesAnalyticsClient>((resolve) => {
  resolveReady = resolve;
});

type StoriesViewEvent =
  | { event: 'bnp_story_viewed'; properties: { story_id: string } }
  | { event: 'bnp_publication_viewed'; properties: { publication_id: string } };

/** Sends a Stories event once the client is ready; pages need not wait for the navbar. */
export function trackStoriesEvent({ event, properties }: StoriesViewEvent): void {
  void ready.then((client) => client.track(event, { ...properties, source_app: 'stories' }));
}

/**
 * In PSP there is no StoriesNavBar to load the client (the shell renders the nav),
 * so the first view loads the shell-mode client itself — when the flag is on.
 */
const usePspAnalytics = (): void => {
  const { featureFlags } = useFeatureFlagStore();
  const isEnabled = isPspMounted() && !!featureFlags.get('product-budget-feature-enable-posthog');
  useEffect(() => {
    if (!isEnabled) return;
    loadAnalytics()
      .then(({ client }) => resolveReady(client))
      .catch((err: unknown) => {
        console.error('Failed to load @opengov/analytics:', err);
      });
  }, [isEnabled]);
};

/** Fires `event` once per id when the page loads, standalone or in PSP. */
export function useTrackStoriesView(view: StoriesViewEvent | null): void {
  usePspAnalytics();
  const id = view && Object.values(view.properties)[0];
  useEffect(() => {
    if (view && id) trackStoriesEvent(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view?.event, id]);
}

type UseStoriesAnalyticsParams = {
  session: AnalyticsSession;
  entity: AnalyticsEntity;
  isPosthogEnabled: boolean;
  isPspMode: boolean;
  pathname?: string;
  routePattern?: string;
};

/**
 * The client StoriesNavBar passes to bnp-core's sign-in and sign-out helpers, and
 * the client that records pageviews. `undefined` — so nothing is sent — unless all of:
 *  - `product-budget-feature-enable-posthog` is on,
 *  - Stories is standalone (in PSP the shell owns sessions and page views; see
 *    useTrackStoriesView for the events Stories still sends there),
 *  - `ANALYTICS_KEY` is present, which only the integration build injects.
 */
export function useStoriesAnalytics({
  session,
  entity,
  isPosthogEnabled,
  isPspMode,
  pathname,
  routePattern
}: UseStoriesAnalyticsParams): SessionAnalyticsClient | undefined {
  const isEnabled = isPosthogEnabled && !isPspMode && !!analyticsEnv().ANALYTICS_KEY;
  const [loadedAnalytics, setLoadedAnalytics] = useState<LoadedAnalytics | null>(null);

  useEffect(() => {
    if (!isEnabled) return;
    let cancelled = false;
    loadAnalytics()
      .then((result) => {
        if (!cancelled) setLoadedAnalytics(result);
      })
      .catch((err: unknown) => {
        console.error('Failed to load @opengov/analytics:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isEnabled]);

  // Re-runs when the user or entity changes (e.g. after an entity switch).
  useEffect(() => {
    if (!loadedAnalytics) return;
    loadedAnalytics.runtime.identifyStoriesUser(loadedAnalytics.client, session, entity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedAnalytics, session.uuid, entity.uuid]);

  const client = isEnabled && loadedAnalytics ? loadedAnalytics.client : undefined;

  useEffect(() => {
    if (client) resolveReady(client);
  }, [client]);

  // Once per pattern, not per pathname: `/story/1` and `/story/2` are one route.
  // `pathname` is read for product and module; it is not a dependency on purpose.
  useEffect(() => {
    if (!client || !loadedAnalytics || !routePattern) return;
    loadedAnalytics.runtime.trackStoriesPage(client, pathname ?? '', routePattern);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, loadedAnalytics, routePattern]);

  return client;
}
